import { useContext, useEffect, useState } from "react";
import { Button, Dropdown, Empty, Modal as AntModal, Table, message } from "antd";
import {
  CloseOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import AppContext from "../../../../context/AppContext";
import { Modal } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { isPermissionBypassActive } from "../../../../utils/permissionBypass";
import {
  isScheduledTaskForbidden,
  isScheduledTaskTerminal,
  scheduledTaskListPollInterval,
  scheduledTaskRunFromList,
  useCasinoScheduledTaskRun,
  useCasinoScheduledTaskRuns,
  useTriggerCasinoScheduledTask,
} from "../../../../queries/casino/useCasinoScheduledTasks";
import {
  getScheduledTaskColumns,
  purposeLabel,
  triggerErrorCopy,
  type ScheduledTaskRun,
} from "../../ScheduledTasks/scheduledTaskFormatters";

/**
 * The games-catalog page only ever runs the catalog sync — operators come
 * here when games are missing (CASENG-1058). Every other purpose stays on
 * the Scheduled Tasks admin page, so no purpose picker.
 */
const SCHEDULER_PURPOSE = "load-casino-games";

type AppContextShape = {
  adminPermissions?: string[] | null;
};

function CasinoGamesSchedulerMenu({
  onRunFinished,
}: {
  /** Fired when a tracked run lands on `success` — the catalog just changed. */
  onRunFinished?: () => void;
}) {
  const { adminPermissions } = (useContext(AppContext) ?? {}) as AppContextShape;

  const bypass = isPermissionBypassActive();
  const canTrigger =
    bypass ||
    Boolean(adminPermissions?.includes("casino.scheduled_tasks.trigger"));
  const canReadLog =
    bypass || Boolean(adminPermissions?.includes("casino.scheduled_tasks.read"));

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  // `startedAt` bounds both polls below to the client cap.
  const [tracked, setTracked] = useState<{
    correlationId: string;
    startedAt: number;
  } | null>(null);
  const trackedCorrelationId = tracked?.correlationId ?? null;
  const trackedSince = tracked?.startedAt ?? 0;

  const triggerMutation = useTriggerCasinoScheduledTask();
  const runQuery = useCasinoScheduledTaskRun(
    trackedCorrelationId,
    trackedSince,
  );
  const runForbidden = isScheduledTaskForbidden(runQuery.error);

  // Environments without the correlation_id GSI 500 on the single-run lookup
  // (pending infra rollout, not a code bug) — fall back to polling the
  // history list and finding the tracked run there. Only for users who can
  // read the log: for anyone else the list 403s too, and the effect below
  // ends tracking on the 403 instead of polling a forbidden endpoint.
  const fallbackPolling =
    canReadLog &&
    Boolean(trackedCorrelationId) &&
    runQuery.isError &&
    !runForbidden;
  const runsQuery = useCasinoScheduledTaskRuns(SCHEDULER_PURPOSE, {
    enabled: logOpen || fallbackPolling,
    refetchInterval: fallbackPolling
      ? scheduledTaskListPollInterval(trackedCorrelationId, trackedSince)
      : undefined,
  });

  const logForbidden = isScheduledTaskForbidden(runsQuery.error);
  // A 403 on whichever read is tracking the run means its progress can't be
  // observed at all. The trigger itself went through, so stop the spinner
  // and say why rather than polling forever.
  const trackingForbidden =
    Boolean(trackedCorrelationId) &&
    (runForbidden || (fallbackPolling && logForbidden));

  const trackedRun =
    runQuery.data ??
    (fallbackPolling
      ? scheduledTaskRunFromList(runsQuery.data, trackedCorrelationId)
      : null);
  const trackedStatus = trackedRun?.status;

  const refetchRuns = runsQuery.refetch;
  useEffect(() => {
    if (!trackedCorrelationId || !isScheduledTaskTerminal(trackedStatus)) {
      return;
    }
    if (trackedStatus === "success") {
      message.success(AppStrings.CASINO_SCHEDULED_TASKS_RUN_SUCCEEDED);
      onRunFinished?.();
    } else {
      message.error(AppStrings.CASINO_SCHEDULED_TASKS_RUN_FAILED);
    }
    // Clearing the id stops both polls; the messages above are the terminal
    // signal, so nothing needs the run afterwards.
    setTracked(null);
  }, [trackedCorrelationId, trackedStatus, onRunFinished]);

  useEffect(() => {
    if (!trackingForbidden) {
      return;
    }
    message.warning(AppStrings.CASINO_SCHEDULED_TASKS_LOG_FORBIDDEN);
    setTracked(null);
  }, [trackingForbidden]);

  useEffect(() => {
    // 403 renders in the modal body instead — a toast would double-message.
    if (logOpen && runsQuery.isError && !logForbidden) {
      message.error(AppStrings.CASINO_SCHEDULED_TASKS_LOAD_ERROR);
    }
  }, [logOpen, runsQuery.isError, logForbidden]);

  const handleTrigger = () => {
    setConfirmOpen(false);
    triggerMutation.mutate(SCHEDULER_PURPOSE, {
      onSuccess: (data: { correlation_id?: string | null } | null) => {
        message.success(AppStrings.CASINO_SCHEDULED_TASKS_TRIGGERED);
        const correlationId = data?.correlation_id;
        setTracked(
          correlationId ? { correlationId, startedAt: Date.now() } : null,
        );
        if (logOpen) {
          refetchRuns();
        }
      },
      onError: (error: unknown) => {
        message.error(triggerErrorCopy(error));
      },
    });
  };

  const items = [
    ...(canTrigger
      ? [
          {
            key: "run-scheduler",
            icon: <PlayCircleOutlined />,
            label: AppStrings.CASINO_SCHEDULED_TASKS_RUN_SCHEDULER,
          },
        ]
      : []),
    ...(canReadLog
      ? [
          {
            key: "update-log",
            icon: <UnorderedListOutlined />,
            label: AppStrings.CASINO_SCHEDULED_TASKS_UPDATE_LOG,
          },
        ]
      : []),
  ];

  // Same convention as the rest of the page: ungated actions disappear
  // entirely rather than rendering disabled.
  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown
        menu={{
          items,
          onClick: ({ key }) => {
            if (key === "run-scheduler") {
              setConfirmOpen(true);
            } else if (key === "update-log") {
              setLogOpen(true);
            }
          },
        }}
        trigger={["click"]}
      >
        <Button
          type="default"
          className="casino-games-scheduler-btn"
          icon={<SyncOutlined spin={Boolean(trackedCorrelationId)} />}
          aria-label={AppStrings.CASINO_SCHEDULED_TASKS}
          data-testid="casino-games-scheduler-menu"
        />
      </Dropdown>

      <Modal
        open={confirmOpen}
        size="sm"
        title={AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER_CONFIRM_TITLE}
        onClose={() => setConfirmOpen(false)}
        primaryAction={{
          label: AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER,
          onClick: handleTrigger,
          loading: triggerMutation.isPending,
        }}
        secondaryAction={{
          label: AppStrings.CANCEL,
          onClick: () => setConfirmOpen(false),
          disabled: triggerMutation.isPending,
        }}
      >
        {formatString(
          AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER_CONFIRM_DESCRIPTION,
          { purpose: purposeLabel(SCHEDULER_PURPOSE) },
        )}
      </Modal>

      <AntModal
        open={logOpen}
        rootClassName="casino-games-modal-root"
        className="casino-games-modal"
        title={
          <span className="casino-games-modal-title">
            {AppStrings.CASINO_SCHEDULED_TASKS_UPDATE_LOG}
          </span>
        }
        closeIcon={<CloseOutlined />}
        onCancel={() => setLogOpen(false)}
        footer={null}
        width={760}
      >
        {logForbidden ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={AppStrings.CASINO_SCHEDULED_TASKS_LOG_FORBIDDEN}
          />
        ) : (
          <Table<ScheduledTaskRun>
            rowKey="run_id"
            columns={getScheduledTaskColumns()}
            dataSource={(runsQuery.data?.runs ?? []) as ScheduledTaskRun[]}
            loading={runsQuery.isFetching}
            pagination={false}
            size="middle"
            scroll={{ x: true }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={AppStrings.CASINO_SCHEDULED_TASKS_LOG_EMPTY}
                />
              ),
            }}
          />
        )}
      </AntModal>
    </>
  );
}

export default CasinoGamesSchedulerMenu;
